import type { Guide } from "./types.js";
import { Lead, H2, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "troubleshooting",
  title: "Troubleshooting common errors",
  category: "Troubleshooting",
  lastUpdated: "2026-06-10",
  description: "Symptoms + fixes for issues you'll actually hit: scrape failures, Hebrew display bugs, cache misses, OAuth gotchas, asset upload errors.",
  relatedGuides: ["credentials-reference", "asset-storage"],
  readTimeMin: 6,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Most issues fall into one of three categories: credentials not configured / expired, free-tier rate limits, or known quirks we've documented but not yet automated around. Below: symptom → root cause → fix.
      </Lead>

      <H2>SerpAPI / scrape issues</H2>
      <Ul>
        <Li>
          <strong>Symptom</strong>: "Scrape competitors" returns 0 extracts.
          <br/><strong>Cause</strong>: free-tier Firecrawl is WAF-blocked on every major Israeli HMO (Clalit, Maccabi, Meuhedet, Leumit, Sheba). Pre-filtered automatically.
          <br/><strong>Fix</strong>: nothing — the SerpAPI fetch widens to <C>num: 20</C> when filtering is on, so you should still get 5+ scrapable URLs. If everything is blocked, the keyword is in a niche dominated by HMOs and isn't a good fit. Pass <C>--include-blocked</C> via CLI to override (and accept the WAF failures).
        </Li>
        <Li>
          <strong>Symptom</strong>: SerpAPI top-1 result doesn't match what I see in Google.
          <br/><strong>Cause</strong>: SerpAPI's free tier returns cached SERPs (sometimes days old).
          <br/><strong>Fix</strong>: upgrade SerpAPI plan, OR cross-validate via DataForSEO once configured.
        </Li>
      </Ul>

      <H2>Claude / brief / produce</H2>
      <Ul>
        <Li>
          <strong>Symptom</strong>: brief regeneration costs more than expected.
          <br/><strong>Cause</strong>: brief generator caching doesn't hit <C>cache_read</C> — fewer than 2 <C>cache_control</C> blocks per Anthropic's apparent minimum.
          <br/><strong>Fix</strong>: known issue, low impact (brief is a single-call use case). Don't try to add cache_control to the brief prompt — it's currently slightly WORSE than no caching when configured wrong. Production-engine caching DOES work (verified).
        </Li>
        <Li>
          <strong>Symptom</strong>: meta description comes out at ~125 chars (spec wants 140-160).
          <br/><strong>Cause</strong>: Claude reliably undershoots even with explicit char-count guidance.
          <br/><strong>Fix</strong>: already handled — the meta generator runs a 2-attempt retry with explicit feedback. Usually lands in range on attempt 2.
        </Li>
        <Li>
          <strong>Symptom</strong>: produced article still contains em dashes (—) despite the humanization rules.
          <br/><strong>Cause</strong>: Claude occasionally slips. The rules are in <C>BRAND_VOICE_PROMPT_BLOCK</C> which is cached as a system-prompt slice — usually highly reliable, but not 100% on every output.
          <br/><strong>Fix</strong>: search the produced markdown for <C>—</C> (em dash) and <C>–</C> (en dash). If you find any in the body, replace with commas, periods, or restructure. If it happens regularly, consider tightening the rule with a more specific instruction or adding a post-production scrubber that auto-replaces dashes.
        </Li>
        <Li>
          <strong>Symptom</strong>: produced article contains a banned AI-tell phrase (<C>חשוב לציין</C>, <C>delve into</C>, <C>tapestry</C>, etc.).
          <br/><strong>Cause</strong>: same as above — Claude slipped despite the explicit blacklist.
          <br/><strong>Fix</strong>: manual edit. Track recurring offenders so we can either tighten the prompt or build a post-production validator.
        </Li>
        <Li>
          <strong>Symptom</strong>: a cannibalization-grouped secondary keyword didn't appear in the produced article body.
          <br/><strong>Cause</strong>: Claude misjudged where to place it, or skipped it under word-count pressure.
          <br/><strong>Fix</strong>: open the brief's <C>secondaryKeywords</C> list, search the article markdown for each phrase. Missing ones can be manually woven in, OR (when this happens often) add a post-production validator that re-prompts on misses.
        </Li>
        <Li>
          <strong>Symptom</strong>: <C>ANTHROPIC_API_KEY is not set</C> error even though it's in <C>.env</C>.
          <br/><strong>Cause</strong>: dotenv shadow-env bug. Your shell may have <C>ANTHROPIC_API_KEY=""</C> exported (from a prior project).
          <br/><strong>Fix</strong>: all entry points use <C>dotenv.config(&#123; override: true &#125;)</C> to fix this. If you see it on a NEW entry point, add <C>override: true</C> to the dotenv config there.
        </Li>
      </Ul>

      <H2>Distribution channel errors</H2>
      <Ul>
        <Li>
          <strong>YouTube — "Authenticate" hangs at "Waiting for browser..."</strong>
          <br/><strong>Cause</strong>: the MCP listens on 127.0.0.1:3456 locally. Works on local dev; doesn't work when the app is deployed remotely (your laptop's browser can't reach EC2's 127.0.0.1).
          <br/><strong>Fix</strong>: SSH tunnel <C>localhost:3456 → EC2:3456</C> while running Authenticate. Or wait for the server-driven OAuth migration. See Credentials → YouTube → "Deployment caveat" section.
        </Li>
        <Li>
          <strong>Instagram — "IG container create failed" or "image URL not reachable"</strong>
          <br/><strong>Cause</strong>: usually the image URL isn't publicly reachable. IG doesn't accept multipart uploads — only fetchable URLs.
          <br/><strong>Fix</strong>: as of 2026-05-26 the IG distribute route auto-resolves the hero URL from <C>keyword_assets.sanity_url</C> (uploaded by image-gen). If you see this error, EITHER the hero was never uploaded to Sanity (re-run <C>keywords images generate &lt;id&gt;</C>) OR your SANITY_API_TOKEN lacks Editor permissions (regenerate at sanity.io/manage). See the Asset storage guide.
        </Li>
        <Li>
          <strong>WhatsApp — message fails with "outside service window"</strong>
          <br/><strong>Cause</strong>: free-form text is only deliverable within 24h of the recipient's last inbound message to you. Outside that window, only approved templates work.
          <br/><strong>Fix</strong>: use the "template" mode in the WhatsApp dropdown. Requires <C>WHATSAPP_ANNOUNCEMENT_TEMPLATE</C> registered + approved (~24h Meta review).
        </Li>
        <Li>
          <strong>X — "Could not authenticate you" / 401</strong>
          <br/><strong>Cause</strong>: most commonly, the Access Token was generated while logged into the wrong X account. X ties tokens to whoever's logged in at the moment of Generate.
          <br/><strong>Fix</strong>: log into X as the AlternaBe brand account in your browser, regenerate Access Token + Secret in Developer Portal, paste fresh values in Credentials → X (Twitter).
        </Li>
        <Li>
          <strong>TikTok — "access_token_invalid"</strong>
          <br/><strong>Cause</strong>: access tokens last 24h. The orchestrator auto-refreshes when within 60s of expiry, but if the <C>TIKTOK_ACCESS_TOKEN_EXPIRES_AT</C> field is empty (first run) or set incorrectly, the next call may hit 401.
          <br/><strong>Fix</strong>: just re-trigger the distribute. The 401 will surface, and the next attempt should auto-refresh successfully. If repeated failures: the refresh_token expired (365 days), re-run the one-time external OAuth flow.
        </Li>
      </Ul>

      <H2>Content scrubber (em-dashes + banned phrases)</H2>
      <Ul>
        <Li>
          <strong>Symptom</strong>: Errors page shows warnings like <C>"section X had N em/en dash(es) — auto-replaced with commas"</C>.
          <br/><strong>Cause</strong>: Sonnet produced em dashes despite the explicit prompt rules. Defense-in-depth scrubber caught them and replaced with commas.
          <br/><strong>Fix</strong>: nothing required. The article body is already corrected. If you see this on EVERY article, the scrubber is doing what it should and the warnings are just visibility. If you see it 0 times across multiple articles, the prompt's working alone.
        </Li>
        <Li>
          <strong>Symptom</strong>: Errors page shows <C>"section X contains N banned phrase(s) — operator review recommended"</C>.
          <br/><strong>Cause</strong>: Sonnet used a phrase from the banned list in <C>brand/voice/guidelines.md</C> ("Phrases to avoid" section) — e.g. <C>חשוב לציין</C>, <C>delve into</C>, <C>tapestry</C>.
          <br/><strong>Fix</strong>: open the keyword's Article tab and find/replace the phrase. Banned phrases are NOT auto-removed because cutting them mid-sentence breaks grammar; the operator decides whether to restructure or accept.
        </Li>
        <Li>
          <strong>Symptom</strong>: banned phrase list seems wrong (something flagged that's actually fine, or vice versa).
          <br/><strong>Fix</strong>: edit <C>brand/voice/guidelines.md</C> "Phrases to avoid" section directly. Restart the ui-server. Scrubber re-parses the list at boot.
        </Li>
      </Ul>

      <H2>Asset storage (Sanity + Drive)</H2>
      <Ul>
        <Li>
          <strong>Symptom</strong>: <C>Sanity asset upload denied — token needs EDITOR permissions</C>.
          <br/><strong>Cause</strong>: the <C>SANITY_API_TOKEN</C> is read-only (it's the same one used for the Content DB sync, which only needs Viewer).
          <br/><strong>Fix</strong>: regenerate at sanity.io/manage → API → Tokens → Editor role. Paste into Credentials → Sanity. Test still passes for both reads + writes after this.
        </Li>
        <Li>
          <strong>Symptom</strong>: <C>Drive refresh-token exchange failed</C>.
          <br/><strong>Cause</strong>: refresh token was revoked (most commonly because someone modified the OAuth consent screen or deleted the client).
          <br/><strong>Fix</strong>: re-do the Playground flow from the Drive Credentials card setup guide. Paste the new refresh token. Drive uploads are best-effort — Sanity will keep working in the meantime.
        </Li>
        <Li>
          <strong>Symptom</strong>: image-gen run finished but the asset row shows <C>status: partial</C>.
          <br/><strong>Cause</strong>: one destination (usually Drive) failed. The article still distributes because Sanity URL is present.
          <br/><strong>Fix</strong>: check <C>keyword_assets.last_error</C>. Most often Drive — Sanity quota is much higher than Drive's 15GB free tier. Re-run <C>keywords images generate &lt;id&gt;</C> to retry.
        </Li>
      </Ul>

      <H2>UI / display issues</H2>
      <Ul>
        <Li>
          <strong>Hebrew text looks broken in tables/captions</strong>
          <br/><strong>Cause</strong>: <C>dir="auto"</C> mostly handles RTL, but mixed Hebrew+English captions can render weirdly in some browsers.
          <br/><strong>Fix</strong>: refresh the page once. If persistent on a specific component, file it — the convention is to always set <C>dir="auto"</C> on user-content elements.
        </Li>
        <Li>
          <strong>"3,418 chars" reported in tooling but actual text is 1,942 chars</strong>
          <br/><strong>Cause</strong>: Python's <C>len()</C> miscounts unpaired UTF-16 surrogates from JSON responses with Hebrew + emoji. Measurement artifact, not a real bug.
          <br/><strong>Fix</strong>: measure via JS / Node for the source of truth. JS String.length is the source of truth for caption truncation.
        </Li>
      </Ul>

      <H2>Background scheduler issues</H2>
      <Ul>
        <Li>
          <strong>Symptom</strong>: discovery sweeps not running.
          <br/><strong>Cause</strong>: <C>DISCOVERY_AUTOPOLL=off</C> in .env.
          <br/><strong>Fix</strong>: remove that line OR use the manual <C>POST /api/discovery/poll-all</C> route.
        </Li>
        <Li>
          <strong>Symptom</strong>: nightly Content DB sync didn't run.
          <br/><strong>Cause</strong>: the server process restarted between cron firings, OR <C>CONTENT_DB_AUTOSYNC=off</C>.
          <br/><strong>Fix</strong>: click "Refresh now" on the Content DB page. Check process logs for cron startup output.
        </Li>
      </Ul>

      <Callout kind="tip">
        Most credential-related issues are caught by the Test button on each Credentials card. When something breaks, first instinct should be: open <AppLink to="credentials">Credentials</AppLink>, find the relevant card, click Test, read the error.
      </Callout>
    </>
  );
}

export const guide: Guide = { meta, Body };
